import ServicesAPI, {
    CreateCheckout,
    CreateCheckoutPartial,
} from "@/api/services";
import { definitions } from "@/apitypes";
import {
    createArticlePaymentUrls,
    getBrowserFlags,
    getCountryCode,
} from "@/misc/common";
import readConfigurationProperty from "@/misc/readConfigurationProperty";
import { TypedListener } from "@/store";
import { Article, Currency, CheckoutResponse, RebateCode } from "@/types";
import {
    createListenerMiddleware,
    createSlice,
    isAnyOf,
    PayloadAction,
    createAction,
} from "@reduxjs/toolkit";
import { push } from "connected-react-router";
import { omit } from "lodash";
import { getFormValues, setSubmitFailed } from "redux-form";

interface InitialState {
    entity: RebateCode | Article | null;
    selected: Article | null;
    isLoading: boolean;
    checkout: {
        isLoading: boolean;
        error: any;
        entity: CheckoutResponse | null;
    };
    error: any;
}

export const initialState: InitialState = {
    entity: null,
    selected: null,
    error: null,
    isLoading: false,
    checkout: {
        isLoading: false,
        error: null,
        entity: null,
    },
};

// Middlewares
export const articleListener = createListenerMiddleware();
const startArticleListening = articleListener.startListening as TypedListener;

// Actions

export const confirmArticle = createAction(
    "article/confirm",
    (article: Article) => ({ payload: article })
);

// Slice

const articleSlice = createSlice({
    name: "article",
    initialState,
    reducers: {
        selectArticle: (
            state,
            { payload }: PayloadAction<{ article: Article }>
        ) => {
            state.selected = payload.article;
            state.error = undefined;
            state.isLoading = false;
        },
        clearArticle: (state) => {
            state.checkout = initialState.checkout;
            state.entity = initialState.entity;
            state.error = initialState.error;
            state.isLoading = initialState.isLoading;
            state.selected = initialState.selected;
        },
        createRebateCodeSuccess: (state, action: PayloadAction<RebateCode>) => {
            state.entity = action.payload;
        },
        createRebateCodeFailure: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        buyArticleRequest: (state) => {
            state.checkout.isLoading = true;
            state.checkout.error = undefined;
        },
        buyArticleSuccess: (
            state,
            action: PayloadAction<definitions["QvicklyCheckoutResponse"]>
        ) => {
            state.checkout.entity = action.payload;
            state.checkout.isLoading = false;
            state.checkout.error = undefined;
        },
        buyArticleFail: (state, action) => {
            state.checkout.error = action.payload;
            state.checkout.isLoading = false;

            state.isLoading = false;
        },
    },
    extraReducers(builder) {
        builder.addCase(confirmArticle, (state, action) => {
            state.isLoading = true;
            state.error = undefined;
        });
    },
});

export const {
    selectArticle,
    createRebateCodeFailure,
    buyArticleSuccess,
    buyArticleFail,
    createRebateCodeSuccess,
    clearArticle,
} = articleSlice.actions;

// Listeners

startArticleListening({
    matcher: isAnyOf(selectArticle.match),
    effect: async ({ payload }: ReturnType<typeof selectArticle>, api) => {
        // since we selected the service, we don't need
        // any previous checkout
        api.dispatch({ type: "PURGE_PERSISTED" });
        api.dispatch(push(`/articles/${payload.article.Id}/confirm`));
    },
});

startArticleListening({
    predicate: confirmArticle.match,
    effect: async ({ payload }, api) => {
        let rebateCode;
        const company = api.getState().company.data;
        const configuration = api.getState().configuration.data;
        const customerForm: any = getFormValues("customerForm")(api.getState());
        const recipientForm: any = getFormValues("recipientForm")(
            api.getState()
        );

        try {
            rebateCode = await ServicesAPI.createRebateFromArticle({
                ArticleId: payload.Id,
                CompanyId: company?.Id,
                ...customerForm,
                ...(recipientForm?.Email ? { Receiver: recipientForm } : {}),
            });

            api.dispatch(createRebateCodeSuccess(rebateCode));
        } catch (error) {
            api.dispatch(createRebateCodeFailure(error));
            api.dispatch(setSubmitFailed("createArticleForm"));
        }

        try {
            if (
                company?.Id &&
                company.BookingSettings?.PaymentProviderId &&
                rebateCode
            ) {
                const { iOSSafari } = getBrowserFlags();

                const checkoutPayload: CreateCheckoutPartial = {
                    // @ts-ignore
                    CompanyId: company.Id,
                    // @ts-ignore
                    Id: rebateCode.Id,
                    // @ts-ignore
                    PaymentProviderId:
                        company.BookingSettings?.PaymentProviderId,
                    CountryCode: getCountryCode(
                        readConfigurationProperty("language", "se")
                    ),
                    Articles: [
                        {
                            ArticleTypeId: payload.ArticleTypeId,
                            //@ts-ignore
                            InternalReferenceId: rebateCode.Id,
                            Quantity: 1,
                        },
                    ],
                    isArticleCheckout: true,
                    UiMode:
                        configuration.paymentButton || iOSSafari
                            ? "hosted"
                            : "embedded",
                    customerEmail: customerForm?.Customer?.Email,
                };

                const checkoutResponse = await ServicesAPI.createCheckout(
                    checkoutPayload,
                    configuration
                );

                const articleState = api.getState().article;

                api.dispatch(buyArticleSuccess(checkoutResponse));
                api.dispatch(
                    push(`/articles/${articleState?.selected?.Id}/completed`)
                );
            }
        } catch (error: any) {
            const articleState = api.getState().article;

            api.dispatch(setSubmitFailed("createArticleForm"));
            api.dispatch(buyArticleFail(error));
            api.dispatch(
                push(`/articles/${articleState?.selected?.Id}/completed`)
            );
        }
    },
});

export default articleSlice;
